/**
 * Module dependencies.
 */

import { FormList } from 'app/components/organisms/crud/form-types';
import { Checkbox } from 'app/components/ui/checkbox';
import { Combobox } from 'app/components/ui/combobox';
import { formatCurrency } from 'app/core/utils/formatter';
import { useFeature } from 'app/hooks/requests/use-feature-flags';
import { useGetBulkRepositionCandidates } from 'app/hooks/requests/transactions/use-get-bulk-reposition-candidates';
import { cn } from 'app/lib/utils';
import { Transaction } from 'app/types/transaction';
import BigNumber from 'bignumber.js';
import { Trash2 } from 'lucide-react';
import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TFunction } from 'react-i18next';

/**
 * Readable date for a transaction (bank date falls back to the movement date).
 */

function txDate(item: Transaction): string {
  return moment(item.date ?? item.bank_date).format('DD-MM-YYYY');
}

/**
 * Readable description for a transaction.
 */

function txDescription(item: Transaction): string {
  return item.description ?? item.bank_description ?? '';
}

/**
 * `BulkValueRepositionPicker` props.
 */

type PickerProps = {
  form: any;
  data: any;
  translate: TFunction;
};

/**
 * Export `BulkValueRepositionPicker` component.
 *
 * A searchable picker of transactions that, together, offset the source
 * transaction. Picking one moves it into the table below; the movement can only
 * be saved when the amounts balance exactly (source + Σ selected === 0).
 */

export function BulkValueRepositionPicker({ form, data, translate }: PickerProps): JSX.Element {
  const sourceId = data?.id;
  const badgesEnabled = useFeature('transactionBadges');
  const { data: candidates } = useGetBulkRepositionCandidates(sourceId);
  const candidateList = useMemo(() => candidates ?? [], [candidates]);

  const originAmount = useMemo(() => new BigNumber(data?.amount ?? '0'), [data?.amount]);
  const byId = useMemo(() => new Map(candidateList.map(item => [String(item.id), item])), [candidateList]);

  const selectedIds: Array<number | string> = form.watch('targetTransactionIds') ?? [];
  const selected = selectedIds.map(id => byId.get(String(id))).filter(Boolean) as Transaction[];

  // Badges present among the candidates, with how many candidates carry each.
  const badgeOptions = useMemo(() => {
    const counts = new Map<number, { name: string; color: string; count: number }>();

    for (const item of candidateList) {
      for (const badge of item.badges ?? []) {
        const entry = counts.get(badge.id) ?? { name: badge.name, color: badge.color, count: 0 };

        entry.count += 1;
        counts.set(badge.id, entry);
      }
    }

    return Array.from(counts.entries()).map(([id, value]) => ({ id, ...value }));
  }, [candidateList]);

  // Badges actually used to add rows — offered for removal after saving.
  const [usedBadgeIds, setUsedBadgeIds] = useState<number[]>([]);
  const [removeUsedBadges, setRemoveUsedBadges] = useState(false);

  // Keep the payload field in sync so the API can drop the spent badges on save.
  useEffect(() => {
    form.setValue('removeUsedBadgeIds', removeUsedBadges ? usedBadgeIds : [], { shouldDirty: true });
  }, [form, removeUsedBadges, usedBadgeIds]);

  const sum = selected.reduce((acc, item) => acc.plus(item.amount ?? '0'), new BigNumber(0));
  const residual = originAmount.plus(sum);
  const balanced = residual.isZero();

  // Validate on the form so save is blocked until the selection balances.
  const stateRef = useRef({ originAmount, byId });
  stateRef.current = { originAmount, byId };

  useEffect(() => {
    form.register('targetTransactionIds', {
      validate: (ids: Array<number | string> | undefined) => {
        const list = ids ?? [];

        if (list.length === 0) {
          return translate('bankAccounts.bulkReposition.selectAtLeastOne');
        }

        const total = list.reduce(
          (acc: BigNumber, id) => acc.plus(stateRef.current.byId.get(String(id))?.amount ?? '0'),
          stateRef.current.originAmount
        );

        return total.isZero() || translate('bankAccounts.bulkReposition.notBalanced');
      }
    });
  }, [form, translate]);

  const setSelected = (ids: Array<number | string>) => {
    form.setValue('targetTransactionIds', ids, { shouldValidate: true, shouldDirty: true });
  };

  const add = (id: number | string) => {
    if (id == null || selectedIds.some(item => String(item) === String(id))) {
      return;
    }

    setSelected([...selectedIds, id]);
  };

  const remove = (id: number | string) => {
    setSelected(selectedIds.filter(item => String(item) !== String(id)));
  };

  // Add every candidate carrying the picked badge, and remember the badge so it
  // can optionally be removed from those transactions after saving.
  const addByBadge = (badgeId: number) => {
    if (badgeId == null) {
      return;
    }

    const ids = candidateList
      .filter(item => (item.badges ?? []).some(badge => badge.id === badgeId))
      .map(item => item.id);
    const next = [...selectedIds];

    for (const id of ids) {
      if (!next.some(existing => String(existing) === String(id))) {
        next.push(id);
      }
    }

    setSelected(next);
    setUsedBadgeIds(current => (current.includes(badgeId) ? current : [...current, badgeId]));
  };

  const options = candidateList
    .filter(item => !selectedIds.some(id => String(id) === String(item.id)))
    .map(item => ({
      value: item.id,
      label: `${txDate(item)} · ${txDescription(item)} · ${formatCurrency(item.amount)} · ${item.account?.name ?? ''}`,
      transaction: item
    }));

  const error = form.formState.errors?.targetTransactionIds?.message as string | undefined;

  return (
    <div className={'flex flex-col gap-3'}>
      <div>
        <div className={'mb-1 text-xs text-muted-foreground'}>
          {translate('bankAccounts.bulkReposition.addTransactions')}
        </div>

        <Combobox
          filterOption={(input, option: any) => {
            const item: Transaction = option.transaction;
            const haystack = [
              txDescription(item),
              item.account?.name,
              txDate(item),
              String(item.amount),
              formatCurrency(item.amount)
            ]
              .filter(Boolean)
              .join(' ')
              .toLowerCase();

            return haystack.includes(input.toLowerCase());
          }}
          onChange={add}
          options={options}
          placeholder={translate('bankAccounts.bulkReposition.searchPlaceholder')}
          searchable
          value={null}
        />
      </div>

      {/* Add every transaction that carries a badge, in one go. */}
      {badgesEnabled && badgeOptions.length > 0 && (
        <div>
          <div className={'mb-1 text-xs text-muted-foreground'}>
            {translate('bankAccounts.bulkReposition.addByBadge')}
          </div>

          <Combobox
            onChange={value => value != null && addByBadge(Number(value))}
            options={badgeOptions.map(badge => ({
              value: badge.id,
              label: `${badge.name} (${badge.count})`,
              badge
            }))}
            placeholder={translate('bankAccounts.bulkReposition.selectBadge')}
            searchable
            value={null}
          />

          {usedBadgeIds.length > 0 && (
            <label className={'mt-2 flex cursor-pointer items-center gap-2 text-sm text-foreground'}>
              <Checkbox checked={removeUsedBadges} onCheckedChange={value => setRemoveUsedBadges(value === true)} />

              {translate('bankAccounts.bulkReposition.removeUsedBadges')}
            </label>
          )}
        </div>
      )}

      {selected.length > 0 && (
        <div className={'overflow-hidden rounded-lg border border-border'}>
          <table className={'w-full border-collapse text-sm'}>
            <thead>
              <tr className={'border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground'}>
                <th className={'px-3 py-2 font-medium'}>{translate('common.table.columns.date')}</th>

                <th className={'px-3 py-2 font-medium'}>{translate('common.table.columns.description')}</th>

                <th className={'px-3 py-2 font-medium'}>{translate('common.labels.account')}</th>

                <th className={'px-3 py-2 text-right font-medium'}>{translate('common.table.columns.amount')}</th>

                <th className={'px-2 py-2'} />
              </tr>
            </thead>

            <tbody className={'divide-y divide-border'}>
              {selected.map(item => (
                <tr key={item.id}>
                  <td className={'whitespace-nowrap px-3 py-2 text-muted-foreground'}>{txDate(item)}</td>

                  <td className={'px-3 py-2'}>{txDescription(item)}</td>

                  <td className={'whitespace-nowrap px-3 py-2 text-muted-foreground'}>{item.account?.name}</td>

                  <td className={"whitespace-nowrap px-3 py-2 text-right font-['Oswald'] tabular-nums"}>
                    {formatCurrency(item.amount)}
                  </td>

                  <td className={'px-2 py-2 text-right'}>
                    <button
                      aria-label={translate('common.actions.remove')}
                      className={
                        'grid h-7 w-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive'
                      }
                      onClick={() => remove(item.id)}
                      type={'button'}
                    >
                      <Trash2 className={'h-3.5 w-3.5'} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Balance summary */}
      <div className={'flex flex-col gap-1 rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm'}>
        <div className={'flex justify-between'}>
          <span className={'text-muted-foreground'}>{translate('bankAccounts.bulkReposition.source')}</span>

          <span className={"font-['Oswald'] tabular-nums"}>{formatCurrency(originAmount.toString())}</span>
        </div>

        <div className={'flex justify-between'}>
          <span className={'text-muted-foreground'}>{translate('bankAccounts.bulkReposition.selectedTotal')}</span>

          <span className={"font-['Oswald'] tabular-nums"}>{formatCurrency(sum.toString())}</span>
        </div>

        <div className={'flex justify-between border-t border-border pt-1 font-medium'}>
          <span>{translate('bankAccounts.bulkReposition.residual')}</span>

          <span className={cn("font-['Oswald'] tabular-nums", balanced ? 'text-[#1B752B]' : 'text-destructive')}>
            {formatCurrency(residual.toString())}
          </span>
        </div>
      </div>

      {!!error && <div className={'text-sm text-destructive'}>{error}</div>}
    </div>
  );
}

/**
 * Export `bulkValueReposition` form field list.
 */

export const bulkValueReposition = (translate: TFunction, data: any): FormList => [
  {
    type: 'render',
    name: 'targetTransactionIds',
    renderComponent: (props: any) => <BulkValueRepositionPicker {...props} data={data} translate={translate} />
  },
  {
    type: 'datePickerField',
    name: 'date',
    label: translate('common.labels.date'),
    rules: undefined
  },
  {
    type: 'inputField',
    name: 'description',
    label: translate('common.labels.description'),
    rules: undefined
  }
];
